import { ABExplorationKeyWords, SupportedMeasureType } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { useAbExplorationEvaluateMultipleMetric } from "@src/hooks/ab-testing/explore-evaluate";
import { useGetGeneralField } from "@src/hooks/ab-testing/use-get-general-field";
import { IMetricEvaluationResult } from "@src/types/ab-testing-exploration";
import { MappingMetricEvaluate } from "@src/util/ab-testing/funnel/mapping-metric-evaluate";
import { getMetricEvaluationRequireMeasures } from "@src/util/ab-testing/report-data/get-metric-evaluation-require-measures";
import { convertArrayToObjectBySpecificKey } from "@src/util/common/array-to-object";
import { FormInstance } from "antd";
import { Dispatch, memo, SetStateAction, useEffect } from "react";

const RetentionSignificanceInternal: React.FC<{
    form: FormInstance<any>;
    result?: Record<string, any>[];
    setSignificanceResult: Dispatch<
        SetStateAction<Record<string, Record<string, Record<string, IMetricEvaluationResult>>>>
    >;
    applyValue: {
        breakdownBy: string;
        breakdownValue: string[];
        significant: number;
    };
    fullMetrics: { mean: string; std?: string }[];
    metricType: "retention" | "sum_retention";
}> = ({ form, result, applyValue, fullMetrics, setSignificanceResult, metricType }) => {
    const { variants, baseVariant } = useGetGeneralField({ form });
    const mergedRequestBatchPayload =
        result?.length && result?.length > 0
            ? fullMetrics.map((item) => {
                  const mappingMetricEvaluate = new MappingMetricEvaluate({
                      significanceLevel: applyValue.significant,
                      breakdownBy: applyValue.breakdownBy ? [applyValue.breakdownBy] : null,
                      baseVariant: baseVariant,
                      variants: variants ?? [],
                      measureValue: item.mean,
                      measureType:
                          metricType === "retention" ? SupportedMeasureType.PROPORTION : SupportedMeasureType.MEAN,
                  });
                  mappingMetricEvaluate.setLoadResult(result ?? []);
                  const fullRequestPayload = mappingMetricEvaluate.getMetricEvaluateRequest(
                      {
                          getOriginalSampleSize: true,
                      },
                      item.std
                          ? {
                                customMean: item.mean,
                                customStd: item.std,
                            }
                          : undefined,
                  );
                  return {
                      metric: fullRequestPayload.metric,
                      data: fullRequestPayload.data,
                  };
              })
            : [];

    const { result: metricEvaluationResult } = useAbExplorationEvaluateMultipleMetric({
        baseGroup: baseVariant,
        variantGroups: variants ?? [],
        metricType: metricType === "retention" ? SupportedMeasureType.PROPORTION : SupportedMeasureType.MEAN,
        significanceLevel: applyValue.significant ?? 0,
        data: mergedRequestBatchPayload,
        isSkip: !result || result?.length === 0 || !variants || variants?.length === 0 || !baseVariant,
        batch: true,
    });

    useEffect(() => {
        if (!metricEvaluationResult) return;
        const breakdownValueList =
            applyValue.breakdownValue && applyValue.breakdownValue.length > 0 ? applyValue.breakdownValue : ["-"];
        const mappingSignificanceResult: Record<
            string,
            Record<string, Record<string, IMetricEvaluationResult>>
        > = breakdownValueList.reduce((output, breakdownValue) => {
            output[breakdownValue] = {};

            metricEvaluationResult?.forEach((item) => {
                const dataMatchWithBatchId = (item.data ?? []).find((_item) => _item.batch_id === breakdownValue);
                const dataFinalWithSampleSize = dataMatchWithBatchId?.data.map((_item) => {
                    const { toEvaluatingData } = getMetricEvaluationRequireMeasures(
                        _item.metric,
                        SupportedMeasureType.PROPORTION,
                        undefined,
                        true,
                        true,
                    );

                    const dataToEvaluate = result?.find((_item2) => {
                        if (!applyValue.breakdownBy) {
                            return _item2["exp_group"] === _item.variant_group;
                        }
                        return (
                            _item2["exp_group"] === _item.variant_group &&
                            _item2[applyValue.breakdownBy] === breakdownValue
                        );
                    });
                    return {
                        ..._item,
                        ...toEvaluatingData(_item.variant_group, dataToEvaluate),
                        [ABExplorationKeyWords.SAMPLE_SIZE]: dataToEvaluate?.[ABExplorationKeyWords.SAMPLE_SIZE],
                    };
                });

                output[breakdownValue][item.metric] = {
                    ...convertArrayToObjectBySpecificKey(dataFinalWithSampleSize ?? [], "variant_group"),
                };
            });
            return output;
        }, {} as Record<string, any>);

        setSignificanceResult(mappingSignificanceResult);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify([applyValue.breakdownValue, metricEvaluationResult, result])]);
    return null;
};

export const RetentionSignificance = memo(RetentionSignificanceInternal, (prevProps, nextProps) => {
    return (
        JSON.stringify([prevProps.result, prevProps.applyValue, prevProps.fullMetrics]) ===
        JSON.stringify([nextProps.result, nextProps.applyValue, nextProps.fullMetrics])
    );
});
